import { ref, computed, provide } from "vue";
import { injectWithSelf } from "~/utils/miscellaneous";
import MyAxios from "~/lib/myAxios";
import GraphQL from "~/lib/graphql";
import isEmpty from "lodash/isEmpty";
import DataLoader from "~/lib/dataloader";
import { getErrors } from "~/utils/service-error-handling";
import { pascalCase } from "~/utils/miscellaneous";
import mapKeys from "lodash/mapKeys";

const isAnyAllRequestComputedConfig = {
  any: {
    arrayPrototypeMethod: "some",
  },
  all: {
    arrayPrototypeMethod: "every",
  },
};

function getAllComputedProperties(allRequestDetails) {
  const allRequestIds = computed(() => Object.keys(allRequestDetails.value));

  const getIsAnyAllRequestComputed = (status, type) => {
    status = status instanceof Array ? status : [status];

    const { arrayPrototypeMethod } = isAnyAllRequestComputedConfig[type];

    return computed(() =>
      allRequestIds.value[arrayPrototypeMethod]((requestId) =>
        status.includes(allRequestDetails.value[requestId].status)
      )
    );
  };

  return {
    _allRequestIds: allRequestIds,
    _isAnyRequest: {
      pending: getIsAnyAllRequestComputed("pending", "any"),
      fulfilled: getIsAnyAllRequestComputed("fulfilled", "any"),
      rejected: getIsAnyAllRequestComputed("rejected", "any"),
    },
    _isAllRequest: {
      pending: getIsAnyAllRequestComputed("pending", "all"),
      fulfilled: getIsAnyAllRequestComputed("fulfilled", "all"),
      rejected: getIsAnyAllRequestComputed("rejected", "all"),
    },
  };
}

class AxiosAffectedComponentBase {
  constructor() {
    this._allRequestDetails = ref({});
    Object.assign(this, getAllComputedProperties(this._allRequestDetails));
  }
  setRequestDetails(requestId, curRequestDetails) {
    this._allRequestDetails.value[requestId] = curRequestDetails;
    this.onSetRequestDetails(requestId, curRequestDetails);
  }
  onBeforeRequest() {
    //child class can override this method
    if (this._isAnyRequest.pending.value) return;

    this._allRequestDetails.value = {};
  }
  onRequestComplete() {
    //child class can override this method
  }
  onSetRequestDetails() {
    //child class can override this method
  }
  getPublicProperties() {
    //child class must override this method
  }
}

const axiosAffectedComponentClasses = {
  AxiosLoader: class extends AxiosAffectedComponentBase {
    constructor(...args) {
      super(...args);
      this.isLoading = ref(false);
    }
    onBeforeRequest() {
      this.isLoading.value = true;
    }
    onRequestComplete() {
      //We want axios response to be stored first at the graphql call, then we want isLoading to be set to false
      setTimeout(
        () => (this.isLoading.value = this._isAnyRequest.pending.value),
        50
      );
    }
    getPublicProperties() {
      return {
        isLoading: this.isLoading,
      };
    }
  },
  AxiosServiceConfirmationData: class extends AxiosAffectedComponentBase {
    constructor(...args) {
      super(...args);
      this.serviceConfirmationData = ref([]);
    }
    onBeforeRequest() {
      super.onBeforeRequest();

      if (isEmpty(this._allRequestDetails.value)) this.serviceConfirmationData.value = [];
    }
    onSetRequestDetails(requestId, { status, data }) {
      if (!data || !(data.data && data.config)) return;

      const index = this.serviceConfirmationData.value.findIndex(
        ({ requestId: curRequestId }) => curRequestId == requestId
      );
      this.serviceConfirmationData.value[
        index == -1 ? this.serviceConfirmationData.value.length : index
      ] = {
        requestId,
        errorHeadingLabelData: {
          ...mapKeys(data.config.meta, (val, key) => 'meta' + pascalCase(key)),
          backendName: data.config.backendName
        },
        response: data.data,
        errors: getErrors(data.data),
        status
      };
    }
    getPublicProperties() {
      return {
        serviceConfirmationData: this.serviceConfirmationData,
      };
    }
  },
  AxiosIsError: class extends AxiosAffectedComponentBase {
    constructor(...args) {
      super(...args);
      this.hasError = computed(() =>
        Object.values(this._allRequestDetails.value).some(
          ({ status }) => status == "rejected"
        )
      );
    }
    getPublicProperties() {
      return {
        hasError: this.hasError,
      };
    }
  },
};

const defaultAxiosAffectedComponentKeys = Object.keys(
  axiosAffectedComponentClasses
);

function allAxiosAffectedComponentCommonObj(axiosAffectedComponents) {
  return Object.keys(axiosAffectedComponents).reduce(
    (commonObj, componentKey) => {
      Object.assign(
        commonObj,
        axiosAffectedComponents[componentKey].getPublicProperties()
      );
      return commonObj;
    },
    {}
  );
}

class AxiosWithUIEffect extends MyAxios {
  constructor(
    axiosAffectedComponents,
    mockContext,
    isMockEnabled,
    requestConfigAppGlobals,
    backendName
  ) {
    super(mockContext, isMockEnabled, requestConfigAppGlobals, backendName);
    this.__axiosAffectedComponents = axiosAffectedComponents;
  }
  loopAxiosAffectedComponents(callback) {
    Object.keys(this.__axiosAffectedComponents).forEach((componentType) => {
      const componentInstance = this.__axiosAffectedComponents[componentType];
      callback(componentInstance);
    });
  }
  setAllAxiosComponentsRequestDetails(requestId, requestDetails) {
    this.loopAxiosAffectedComponents((componentInstance) =>
      componentInstance.setRequestDetails(requestId, requestDetails)
    );
  }
  callOnBeforeRequestOfAllAxiosComponents(requestId, requestDetails) {
    this.loopAxiosAffectedComponents((componentInstance) =>
      componentInstance.onBeforeRequest(requestId, requestDetails)
    );
  }
  callOnRequestCompleteOfAllAxiosComponents(requestId, requestDetails) {
    this.loopAxiosAffectedComponents((componentInstance) =>
      componentInstance.onRequestComplete(requestId, requestDetails)
    );
  }
  onBeforeRequest({ requestId }) {
    const requestDetails = {
      status: "pending",
      data: null,
    };
    this.callOnBeforeRequestOfAllAxiosComponents(requestId, requestDetails);
    this.setAllAxiosComponentsRequestDetails(requestId, requestDetails);
  }
  onRequestComplete(isError, { requestId }, data) {
    const requestDetails = {
      status: isError ? "rejected" : "fulfilled",
      data,
    };
    this.callOnRequestCompleteOfAllAxiosComponents(requestId, requestDetails);
    this.setAllAxiosComponentsRequestDetails(requestId, requestDetails);
  }
}

function provideAxiosAffectedComponents(
  componentKeys = defaultAxiosAffectedComponentKeys,
  app
) {
  if (!componentKeys?.length)
    throw Error("Please provide http affected component keys");

  componentKeys.forEach((componentKey) => {
    const componentClass = axiosAffectedComponentClasses[componentKey];
    (app?.provide || provide)(componentKey, new componentClass());
  });

  return injectAxiosAffectedComponents(undefined, app);
}

function injectAxiosAffectedComponents(
  componentKeys = defaultAxiosAffectedComponentKeys,
  app
) {
  const axiosAffectedComponents = {};

  componentKeys.forEach(
    (componentKey) =>
      (axiosAffectedComponents[componentKey] = injectWithSelf(
        componentKey,
        app
      ))
  );

  return axiosAffectedComponents;
}

const httpUIEffectProvideKey = "httpUIEffect";

function multiServiceTypeFactory(mockContext, gqlContext, backendName) {
  const appGlobals = {
    envSelector: this.envSelector,
  };

  const axiosWithUIEffect = new AxiosWithUIEffect(
    this.axiosAffectedComponents,
    mockContext,
    this.envSelector.isMockEnabled.value,
    appGlobals,
    backendName
  );

  const graphql = new GraphQL(axiosWithUIEffect, gqlContext, appGlobals);

  return {
    axios: axiosWithUIEffect,
    graphql,
  };
}

export function provideHttpUIEffect(
  componentKeys = defaultAxiosAffectedComponentKeys,
  app
) {
  const axiosAffectedComponents = provideAxiosAffectedComponents(
    componentKeys,
    app
  );

  const { mockContext, gqlContext } = injectWithSelf("allRequireContext", app);

  const envSelector = injectWithSelf("envSelector", app);

  let boundMultiServiceTypeFactory = multiServiceTypeFactory.bind({
    axiosAffectedComponents,
    envSelector,
  });

  const { axios, graphql } = boundMultiServiceTypeFactory(mockContext, gqlContext, "Unified");

  const dataloader = new DataLoader({
    multiServiceTypeFactory: boundMultiServiceTypeFactory,
    hostEnv: envSelector.selectedHostEnvConfig.value,
  });

  (app?.provide || provide)(httpUIEffectProvideKey, {
    axios,
    graphql,
    dataloader,
    ...allAxiosAffectedComponentCommonObj(axiosAffectedComponents),
  });

  return injectHttpUIEffect(app);
}

export function injectHttpUIEffect(app) {
  return injectWithSelf(httpUIEffectProvideKey, app);
}
